/**
 * Hero Video Module
 *
 * Handles pause/play functionality for background videos in the hero component.
 *
 * @since 1.0.0
 */

/**
 * Initialize hero video pause/play controls
 *
 * Adds event listeners to pause buttons that toggle the playback
 * of background videos in hero sections.
 *
 * @since 1.0.0
 * @returns {void}
 */
export function initHeroVideo() {
	const pauseButtons = document.querySelectorAll( '.hero-video-pause, .hero-dark-overlay-toggle, .feature-video-spotlight-toggle' );
	const audioButtons = document.querySelectorAll( '.feature-video-spotlight-audio-toggle' );
	const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

	// Exit early if no video control buttons are found.
	if ( pauseButtons.length === 0 && audioButtons.length === 0 ) {
		return;
	}

	/**
	 * Get the actual interactive element for a video control.
	 *
	 * Newer patterns use native buttons directly. Older imported patterns may
	 * still use a core Button wrapper with an inner link.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} button - The video control wrapper or element.
	 * @returns {HTMLElement|null} The interactive control element.
	 */
	function getControlElement( button ) {
		if ( button.matches( 'button, a' ) ) {
			return button;
		}

		return button.querySelector( 'button, .wp-block-button__link, a' );
	}

	/**
	 * Get the element that should receive state classes.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} controlElement - The interactive control element.
	 * @returns {HTMLElement} The element used for visual state classes.
	 */
	function getStateElement( controlElement ) {
		return controlElement.closest( '.wp-block-button' ) || controlElement;
	}

	/**
	 * Whether the control already has native button keyboard behavior.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} controlElement - The interactive control element.
	 * @returns {boolean} Whether the element is a native button.
	 */
	function isNativeButton( controlElement ) {
		return controlElement.tagName.toLowerCase() === 'button';
	}

	/**
	 * Normalize legacy controls without creating nested interactive elements.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} controlElement - The interactive control element.
	 * @returns {void}
	 */
	function prepareControlElement( controlElement ) {
		if ( isNativeButton( controlElement ) ) {
			return;
		}

		controlElement.setAttribute( 'role', 'button' );

		if ( ! controlElement.hasAttribute( 'href' ) && ! controlElement.hasAttribute( 'tabindex' ) ) {
			controlElement.setAttribute( 'tabindex', '0' );
		}
	}

	/**
	 * Update button state to match video playback
	 *
	 * Updates the button text, ARIA labels, and CSS classes to reflect
	 * the current video playback state.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} button - The pause button element.
	 * @param {boolean} isPlaying - Whether the video is currently playing.
	 * @returns {void}
	 */
	function updateButtonState( button, isPlaying ) {
		const controlElement = getControlElement( button );

		if ( ! controlElement ) {
			return;
		}

		const stateElement = getStateElement( controlElement );

		if ( isPlaying ) {
			controlElement.textContent = 'Pause';
			controlElement.setAttribute( 'aria-label', 'Pause background video' );
			controlElement.setAttribute( 'aria-pressed', 'true' );
			stateElement.classList.add( 'is-playing' );
		} else {
			controlElement.textContent = 'Play';
			controlElement.setAttribute( 'aria-label', 'Play background video' );
			controlElement.setAttribute( 'aria-pressed', 'false' );
			stateElement.classList.remove( 'is-playing' );
		}
	}

	/**
	 * Update audio button state to match video audio
	 *
	 * Updates the button text, ARIA labels, and CSS classes to reflect
	 * whether the background video audio is muted.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} button - The audio toggle button element.
	 * @param {boolean} isMuted - Whether the video is currently muted.
	 * @returns {void}
	 */
	function updateAudioButtonState( button, isMuted ) {
		const controlElement = getControlElement( button );

		if ( ! controlElement ) {
			return;
		}

		const stateElement = getStateElement( controlElement );

		if ( isMuted ) {
			controlElement.textContent = 'Unmute';
			controlElement.setAttribute( 'aria-label', 'Unmute background video' );
			controlElement.setAttribute( 'aria-pressed', 'false' );
			stateElement.classList.add( 'is-muted' );
			stateElement.classList.remove( 'is-unmuted' );
		} else {
			controlElement.textContent = 'Mute';
			controlElement.setAttribute( 'aria-label', 'Mute background video' );
			controlElement.setAttribute( 'aria-pressed', 'true' );
			stateElement.classList.add( 'is-unmuted' );
			stateElement.classList.remove( 'is-muted' );
		}
	}

	/**
	 * Get the background video associated with a control button
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} button - The video control button.
	 * @returns {HTMLVideoElement|null} The associated video element.
	 */
	function getVideoElement( button ) {
		const heroSection = button.closest( '.hero-video, .hero-dark-overlay, .feature-video-spotlight' );

		if ( ! heroSection ) {
			return null;
		}

		return heroSection.querySelector( '.wp-block-cover__video-background' );
	}

	/**
	 * Handle pause button click
	 *
	 * Toggles the video playback state and updates the button state
	 * for visual and accessibility feedback.
	 *
	 * @since 1.0.0
	 * @param {Event} event - The click/keydown event object.
	 * @returns {void}
	 */
	function handlePauseClick( event ) {
		event.preventDefault();

		const button = event.currentTarget;
		const videoElement = getVideoElement( button );

		// Exit early if video element is not found.
		if ( ! videoElement ) {
			return;
		}

		// Toggle video playback state.
		if ( videoElement.paused ) {
			const playResult = videoElement.play();

			if ( playResult && typeof playResult.catch === 'function' ) {
				playResult
					.then( () => updateButtonState( button, true ) )
					.catch( () => updateButtonState( button, false ) );
				return;
			}

			updateButtonState( button, true );
		} else {
			videoElement.pause();
			updateButtonState( button, false );
		}
	}

	/**
	 * Handle audio button click
	 *
	 * Toggles the video muted state and starts playback when enabling audio
	 * from a paused state so the click has an immediate effect.
	 *
	 * @since 1.0.0
	 * @param {Event} event - The click/keydown event object.
	 * @returns {void}
	 */
	function handleAudioClick( event ) {
		event.preventDefault();

		const button = event.currentTarget;
		const videoElement = getVideoElement( button );

		// Exit early if video element is not found.
		if ( ! videoElement ) {
			return;
		}

		const shouldEnableAudio = videoElement.muted || videoElement.volume === 0;

		if ( shouldEnableAudio ) {
			videoElement.muted = false;

			if ( videoElement.volume === 0 ) {
				videoElement.volume = 1;
			}

			if ( videoElement.paused ) {
				const playResult = videoElement.play();

				if ( playResult && typeof playResult.catch === 'function' ) {
					playResult
						.then( () => updateAudioButtonState( button, false ) )
						.catch( () => {
							videoElement.muted = true;
							updateAudioButtonState( button, true );
						} );
					return;
				}
			}

			updateAudioButtonState( button, false );
			return;
		}

		videoElement.muted = true;
		updateAudioButtonState( button, true );
	}

	/**
	 * Handle keyboard events on the pause button
	 *
	 * Allows keyboard users to trigger play/pause using Enter or Space keys.
	 *
	 * @since 1.0.0
	 * @param {KeyboardEvent} event - The keydown event object.
	 * @returns {void}
	 */
	function handleKeyDown( event ) {
		// Only handle Enter or Space keys.
		if ( event.key !== 'Enter' && event.key !== ' ' ) {
			return;
		}

		handlePauseClick( event );
	}

	/**
	 * Handle keyboard events on the audio button
	 *
	 * Allows keyboard users to trigger mute/unmute using Enter or Space keys.
	 *
	 * @since 1.0.0
	 * @param {KeyboardEvent} event - The keydown event object.
	 * @returns {void}
	 */
	function handleAudioKeyDown( event ) {
		// Only handle Enter or Space keys.
		if ( event.key !== 'Enter' && event.key !== ' ' ) {
			return;
		}

		handleAudioClick( event );
	}

	// Add click and keyboard listeners to all pause buttons.
	pauseButtons.forEach( ( button ) => {
		const controlElement = getControlElement( button );

		if ( ! controlElement ) {
			return;
		}

		const videoElement = getVideoElement( controlElement );

		// Exit early if video element is not found.
		if ( ! videoElement ) {
			return;
		}

		const syncStateFromVideo = () => updateButtonState( controlElement, ! videoElement.paused );

		// Set initial state and accessibility attributes.
		prepareControlElement( controlElement );

		if ( prefersReducedMotion.matches ) {
			videoElement.pause();
		}

		syncStateFromVideo();

		// Add click and keyboard listeners to toggle playback.
		controlElement.addEventListener( 'click', handlePauseClick );

		if ( ! isNativeButton( controlElement ) ) {
			controlElement.addEventListener( 'keydown', handleKeyDown );
		}

		videoElement.addEventListener( 'play', syncStateFromVideo );
		videoElement.addEventListener( 'pause', syncStateFromVideo );
	} );

	// Add click and keyboard listeners to all audio buttons.
	audioButtons.forEach( ( button ) => {
		const controlElement = getControlElement( button );

		if ( ! controlElement ) {
			return;
		}

		const videoElement = getVideoElement( controlElement );

		// Exit early if video element is not found.
		if ( ! videoElement ) {
			return;
		}

		const syncStateFromVideo = () => updateAudioButtonState( controlElement, videoElement.muted || videoElement.volume === 0 );

		// Set initial state and accessibility attributes.
		prepareControlElement( controlElement );

		syncStateFromVideo();

		// Add click and keyboard listeners to toggle audio.
		controlElement.addEventListener( 'click', handleAudioClick );

		if ( ! isNativeButton( controlElement ) ) {
			controlElement.addEventListener( 'keydown', handleAudioKeyDown );
		}

		videoElement.addEventListener( 'volumechange', syncStateFromVideo );
	} );
}
